import { authFetch, clearAccessToken, BASE_BACKEND_URL } from '../../auth'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { createContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import * as apiStatusCodes from '../../constants/apiStatusCodes'
import { handleTabClick, handleUpdate } from '../helpers/UserConfigHandlers'
import {
  getFeatureRoles,
  getQueryState,
  getUserConfig,
  handleErrors,
  updateUserInfo
} from '../services/apiUserService'

const initialState = {}

const UserInfoContext = createContext(initialState)

const UserInfoProvider = (props) => {
  const { id } = useParams()
  const [isFetching, setFetching] = useState(true)
  const [isEditingUserInfoTab, setEditingUserInfoTab] = useState(false)
  const [userInfoData, setUserInfoData] = useState({})
  const [title, setTitle] = useState('')
  const [failedCreationState, setFailedCreationState] = useState(false)
  const [failedCreationInfo, setFailedCreationInfo] = useState(null)
  const [activeTab, setActiveTab] = useState('Detalles')
  const [formIsValid, setFormIsValid] = useState(true)
  const [featureRoles, setFeatureRoles] = useState([])
  const [warehouses, setWarehouses] = useState([])

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Este es un campo obligatorio.')
      .min(3, 'Asegúrese de cumplir con el mínimo de los 3 caracteres'),
    role: Yup.array()
      .required('Mínimo debe asignar 1 rol disponible.')
      .min(1, 'Mínimo debe asignar 1 rol disponible.'),
    active: Yup.bool(),
    email: Yup.string().required('Este es un campo obligatorio.').email('El formato no es válido.'),
    mobile: Yup.string().notRequired().nullable()
  })

  const formOptions = { resolver: yupResolver(validationSchema) }

  const { register, handleSubmit, reset, setValue, formState, control } = useForm(formOptions)
  const { errors, isValid } = formState

  useEffect(() => {
    if (id) {
      fetchUserInfo(id)
    }
    getFeatureRolesList()
    getWarehouses()
  }, [id])

  const fetchUserInfo = (userId) => {
    setFetching(true)
    const queryParams = getQueryState()
    getUserConfig(userId)
      .then((response) => handleErrors(response))
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setFetching(false)
        setUserInfoData(data)
        setTitle(queryParams ? `Resultados de la búsqueda: ${queryParams}` : `Usuarios`)
        setValue('name', data.full_name)
        setValue('code', data.route_db_ref)
        setValue('email', data.email)
        setValue('mobile', data.mobile)
      })
      .catch((error) => {
        evaluateResponse(error)
        if (error.status === apiStatusCodes.UNAUTHORIZED) {
          clearAccessToken()
          window.location = '/'
        }
      })
  }

  const getFeatureRolesList = () => {
    setFetching(true)
    const queryParams = getQueryState()
    getFeatureRoles()
      .then((response) => handleErrors(response))
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setFetching(false)
        setFeatureRoles(data?.table?.map((e) => ({ value: e.key, role: e.name, label: e.name })))
      })
      .catch((error) => {
        if (error.status === apiStatusCodes.UNAUTHORIZED) {
          clearAccessToken()
          window.location = '/'
        }
      })
  }

  const getWarehouses = () => {
    setFetching(true)
    return authFetch(`${BASE_BACKEND_URL}/warehouses`, {
      method: 'GET',
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setFetching(false)
        setWarehouses(
          data?.result?.map((warehouse) => ({
            value: warehouse.uid,
            label: `${warehouse.uid} - ${warehouse.name}`
          }))
        )
      })
      .catch((error) => {
        if (error !== 'Fetch Aborted by User.') {
          console.error('Error:', error)
        }
      })
  }

  // TODO: Move this to a "global" context so we can use it in other components
  const getRoleLabel = (key) => {
    return featureRoles.find((e) => e.value == key)?.label
  }

  const onSubmit = (data) => {
    updateUserInfo(userInfoData.id, data)
      .then((r) => {
        if (!r) return
        return r.json().then((data) => ({ status: r.status, body: data }))
      })
      .then((obj) => evaluateResponse(obj, false))
      .catch((e) => console.log(e))
  }

  const handleUpdateUser = (event) => {
    const value = handleUpdate(event)
    setEditingUserInfoTab(value)
  }

  const handleTabClickDetails = (event) => {
    const value = handleTabClick(event, isEditingUserInfoTab)
    setActiveTab(value.activeTab)
    setEditingUserInfoTab(value.editing)
  }

  const hideFailedCreationMessage = () => {
    setFailedCreationInfo(null)
  }

  const evaluateResponse = (obj) => {
    switch (obj.status) {
      case 422:
        setFailedCreationInfo('Verifique la informacion ingresada.')
        setTimeout(hideFailedCreationMessage, 9000)
        break
      case 409:
        setFailedCreationInfo(
          'Error al guardar cambios. El código ingresado ya existe. Por favor escoger otro código.'
        )
        setTimeout(hideFailedCreationMessage, 9000)
        break
      case 404:
        setFailedCreationInfo('Servidor fuera de servicio')
        setTimeout(hideFailedCreationMessage, 9000)
        break
      case 500:
        setFailedCreationInfo('Error interno en el sistema')

        setTimeout(hideFailedCreationMessage, 9000)
        break
      default:
        const data = obj.body
        setEditingUserInfoTab(false)

        if (data && data['id']) {
          window.location.href = `/usuarios/${data['id']}`
        } else {
          console.warn('No se pudo redireccionar: ID faltante en la respuesta')
          fetchUserInfo()
        }

        break
    }
  }

  return (
    <UserInfoContext.Provider
      value={{
        isFetching,
        userInfoData,
        title,
        isEditingUserInfoTab,
        activeTab,
        failedCreationInfo,
        createUserParams: props.createUserParams,
        handleUpdateUser,
        handleTabClickDetails,
        onSubmit,
        handleSubmit,
        errors,
        register,
        Controller,
        control,
        featureRoles,
        warehouses,
        getRoleLabel
      }}
    >
      {props.children}
    </UserInfoContext.Provider>
  )
}
export { UserInfoContext, UserInfoProvider }
